import type { FC, PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";

import GeneralLayout from "@loyalty/layouts/GeneralLayout/GeneralLayout";

import { useConfig } from "@jti/app-context/useConfig";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { CasProfileApi, getUser, ReferralApi } from "@jti/rest-api";
import { useSelector } from "@jti/store/reduxHooks";
import { type RootStoreState } from "@jti/store/types";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { NavBar } from "@jti/ui/components/NavBar";
import { SignOut } from "@jti/ui/components/SignOut";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { ResponsiveContainer } from "../../components/ResponsiveContainer/ResponsiveContainer";
import useLoggedUser from "../../hooks/useLoggedUser";
import { redirectToAuthPage } from "../../utils/redirectToAuthPage";

import styles from "./AccountLayout.module.scss";

const AccountLayout: FC<PropsWithChildren<{ isInEditor?: boolean; hideMyAddress?: boolean }>> = ({
  children,
  isInEditor,
  hideMyAddress = false,
}) => {
  const [isRasActive, setRasActive] = useState<boolean>(false);
  const [isLogged] = useLoggedUser();
  const { isLoaded } = useSelector((store: RootStoreState) => store.loyaltyCore);
  const config = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const loginHref = resolveAuthHref({
    route: AUTH_ROUTE_PATHS.login,
    search: { returnUrl: config["base-url"] },
  });
  const onLogout = useCallback(async () => {
    try {
      const user = getUser();
      dispatchTrackingEvent(TrackingEvents.TrackingLogoutSuccess, { user_id: user.uuid || "no id" });

      await CasProfileApi.init().logout();

      redirectToAuthPage(loginHref);

      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  }, [config, loginHref]);

  useEffect(() => {
    const fetchReferralStatus = async () => {
      try {
        const status = await ReferralApi.init().getStatus();
        setRasActive(status);
      } catch (error) {
        // TODO
        console.error("Error fetching referral status:", error);
      }
    };
    fetchReferralStatus();
  }, []);

  useEffect(() => {
    if (isLoaded && !isLogged && !isInEditor) {
      redirectToAuthPage(loginHref);
    }
  }, [isLoaded, isLogged, isInEditor, config, loginHref]);

  return (
    <GeneralLayout isInEditor={isInEditor}>
      <ResponsiveContainer>
        {(isLoaded && isLogged) || isInEditor ? (
          <div data-testid={"accountLayoutContainer"} className={styles.container}>
            <div>
              <NavBar isRasActive={isRasActive} hasLoyalty={true} isExternal={true} hideMyAddress={hideMyAddress} />
              <SignOut logout={onLogout} className={styles.signOut} />
            </div>
            <div className={styles.content}>{children}</div>
          </div>
        ) : null}
      </ResponsiveContainer>
    </GeneralLayout>
  );
};

export default AccountLayout;
